import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PhotoTile from '../components/PhotoTile';
import OfflinePill from '../components/OfflinePill';
import { SkeletonGrid } from '../components/Skeleton';
import { IconPlay } from '../components/Icon';
import { hasMoreMedia, loadMoreMedia, mediaLoaded, useMediaPolling, useMedia, visibleTo } from '../lib/media';
import { useSession } from '../lib/session';
import { useSchedule } from '../lib/content';
import { dayKey, now } from '../lib/time';

type Filter = 'all' | 'mine' | 'hidden';

const PAGE = 24;

/**
 * Artboard 1h — the social surface of the whole site.
 * Every guest sees every upload (D35). One filter, not separate pages; square
 * tiles so six hundred of them scan quickly; the uploader's name on a card foot
 * under the image, with their team's suit mark beside it.
 */
export default function Gallery() {
  const session = useSession();
  const media = useMedia();
  // Other guests' uploads arrive by poll, not by push (D12/D17).
  useMediaPolling(20_000);
  const schedule = useSchedule();
  const [filter, setFilter] = useState<Filter>('all');
  const [count, setCount] = useState(PAGE);
  const [fetching, setFetching] = useState(false);
  const sentinel = useRef<HTMLDivElement>(null);

  const visible = useMemo(() => visibleTo(media, session), [media, session]);

  const filtered = useMemo(() => {
    if (filter === 'mine') return visible.filter((it) => it.uploaderId === session?.userId);
    if (filter === 'hidden') return visible.filter((it) => it.hidden);
    return visible.filter((it) => !it.hidden);
  }, [visible, filter, session]);

  /**
   * Two paginations, one sentinel, and they are not the same thing.
   *
   * `count` is how many of the rows already in the browser get rendered — the
   * cheap one, purely so a thousand tiles do not mount at once. Behind it
   * `loadMoreMedia` fetches the next page from the API, which is the one that
   * decides whether a photo can be reached at all.
   *
   * This used to be the first alone, over a fixed `?limit=100`. The scroll
   * worked perfectly and simply ended after a hundred photos, with no error and
   * no empty state — indistinguishable from having reached the end of the week.
   * Two hundred guests over four days cross that on the first night.
   */
  useEffect(() => { setCount(PAGE); }, [filter]);
  useEffect(() => {
    const node = sentinel.current;
    if (!node) return;
    const io = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      setCount((n) => n + PAGE);
      // Ask the server before the rendered window actually runs out, so the
      // next page is usually already here by the time it is scrolled to. On a
      // resort connection the round trip is the part that shows.
      if (count + PAGE * 2 >= filtered.length && hasMoreMedia()) {
        setFetching(true);
        loadMoreMedia()
          .catch(() => { /* Offline. What is loaded stays; the poll retries. */ })
          .finally(() => setFetching(false));
      }
    }, { rootMargin: '600px' });
    io.observe(node);
    return () => io.disconnect();
  }, [filtered.length, count]);

  const page = filtered.slice(0, count);
  const eventName = (id?: string) => schedule.data?.events.find((e) => e.id === id)?.name;

  // A quiet grouping: today's uploads sit under the event they came from,
  // everything older just says its day.
  const heading = (() => {
    const first = page[0];
    if (!first) return null;
    if (dayKey(first.createdAt) === dayKey(now())) return eventName(first.eventId) ?? 'Today';
    return eventName(first.eventId) ?? 'Earlier this week';
  })();

  // "No photos yet" and "we have not asked yet" look identical from the item
  // count alone, and showing the empty state to someone whose gallery is still
  // loading tells them their photos are gone.
  if (!mediaLoaded() && media.length === 0) return <div className="page"><SkeletonGrid /></div>;

  return (
    <div className="page">
      <OfflinePill />

      <div className={`gal-bar${session?.isAdmin ? ' is-admin' : ''}`}>
        <div className="seg" style={{ fontSize: 12 }}>
          <FilterOption value="all" filter={filter} setFilter={setFilter} label="All" />
          <FilterOption value="mine" filter={filter} setFilter={setFilter} label="Mine" />
          {session?.isAdmin && <FilterOption value="hidden" filter={filter} setFilter={setFilter} label="Hidden" />}
        </div>
        <div style={{ flex: 1 }} />
        {session?.isAdmin && (
          /* The slideshow lives with the photos, not in Profile — it is a thing
             you do to this collection, and the person starting it on the
             reception laptop is already looking at the gallery. */
          <Link className="btn btn-secondary" style={{ flex: 'none', gap: 6, minHeight: 36 }} to="/display">
            <IconPlay size={13} /> Slideshow
          </Link>
        )}
        {/* "so far" means "uploaded so far", and this counts what has been
            *downloaded* — which used to be the same thing when the client asked
            for a fixed hundred and stopped. With paging it climbs as you
            scroll, so while there is more to come it says `249+` rather than
            stating a total it does not know. The exact number needs the
            `visiblePhotos` counter the API keeps for the Now screen, which
            nothing in the app reads yet. */}
        <span className="gal-count">
          {visible.filter((it) => !it.hidden).length}{hasMoreMedia() ? '+' : ''} photos so far
        </span>
      </div>

      {filtered.length === 0 ? (
        <EmptyFor filter={filter} />
      ) : (
        <>
          {heading && (
            <div className="gal-head">
              <h4>{heading}</h4>
              <div className="gal-count">
                {filter === 'mine' ? `${filtered.length} of yours` : `${filtered.length} to look through`}
              </div>
            </div>
          )}
          <div className="gal-grid">
            {page.map((item) => <PhotoTile key={item.id} item={item} showState={filter === 'mine'} />)}
          </div>
          <div ref={sentinel} style={{ height: 1 }} />
          {(count < filtered.length || fetching) && <SkeletonGrid tiles={2} />}
        </>
      )}
    </div>
  );
}

function FilterOption({ value, filter, setFilter, label }: {
  value: Filter; filter: Filter; setFilter: (f: Filter) => void; label: string;
}) {
  return (
    <label className="seg-opt">
      <input type="radio" name="gallery-filter" checked={filter === value} onChange={() => setFilter(value)} />
      {label}
    </label>
  );
}

function EmptyFor({ filter }: { filter: Filter }) {
  if (filter === 'hidden') {
    return (
      <div className="empty">
        <div className="mark" aria-hidden="true">♣</div>
        <h4 style={{ margin: 0 }}>Nothing is hidden</h4>
        <p>Anything you hide from a photo's own screen collects here, and can be put back from the same place.</p>
      </div>
    );
  }
  if (filter === 'mine') {
    // Invite rather than apologise. The All view will not be empty for long.
    return (
      <div className="empty">
        <div className="mark" aria-hidden="true">♥</div>
        <h4 style={{ margin: 0 }}>Nothing of yours yet</h4>
        <p>Whatever you take this week ends up here, with your name on it — and on the big screen at the reception.</p>
        <Link className="btn btn-primary" to="/upload">Add the first one</Link>
      </div>
    );
  }
  return (
    <div className="empty">
      <div className="mark" aria-hidden="true">♦</div>
      <h4 style={{ margin: 0 }}>No photos yet</h4>
      <p>Somebody has to go first. It may as well be you.</p>
      <Link className="btn btn-primary" to="/upload">Add a photo</Link>
    </div>
  );
}
